import { type Client, createClient } from "graphql-ws";

import sfAuth from "~/auth/SFAuth";
import Config from "~/config";
import { shouldRecreateSubscriptionClient } from "~/helpers/describeSubscriptionClose";
import getHeaders from "~/services/graphqlHeaders";

/** Derive the WebSocket URL from the HTTP GraphQL endpoint. */
const getWsUrl = () => Config.endpoint?.replace(/^http/, "ws") ?? "";

let recreatedSinceConnect = false;

const build = (): Client => {
	const created = createClient({
		connectionAckWaitTimeout: 10_000,
		connectionParams: async () => {
			await sfAuth.checkSession();
			return getHeaders();
		},
		retryAttempts: 5,
		// graphql-ws only retries what shouldRetry accepts, and its default rejects the plain Event a network drop raises.
		shouldRetry: () => true,
		url: getWsUrl()
	});
	created.on("connected", () => {
		recreatedSinceConnect = false;
	});
	return created;
};

let client = build();
const listeners = new Set<() => void>();

const getSubscriptionClient = (): Client => client;

export const onSubscriptionClientChange = (listener: () => void): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

// graphql-ws 6 throws out of the operation's retry loop for a 4401, so the failed subscription never returns on its own.
export const recreateSubscriptionClientOnUnauthorized = (closeCode: number | undefined): boolean => {
	if (!shouldRecreateSubscriptionClient(closeCode, recreatedSinceConnect)) return false;

	recreatedSinceConnect = true;
	const previous = client;
	client = build();
	void previous.dispose();
	for (const listener of [...listeners]) listener();
	return true;
};

export const disposeSubscriptionClient = async (): Promise<void> => {
	await client.dispose();
};

export default getSubscriptionClient;
